import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Award, Printer, ArrowLeft, FileDown, Layers } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf, downloadBulkCertificatesPdf } from "@/lib/pdf";

const CERT_LABELS = {
  semester1: "للفصل الدراسي الأول",
  semester2: "للفصل الدراسي الثاني",
  end_year: "نهاية العام الدراسي",
};

// نفس الألوان والترتيب والتسميات المعتمَدة فعليًا في GRADE_BANDS
// (backend/logic.py) — المصدر الوحيد لتلوين درجات الروضة/الابتدائي في كل
// أنحاء النظام. أي تعديل مستقبلي على GRADE_BANDS هناك يجب نقله هنا يدويًا.
const GRADE_BAND_LEGEND = [
  { label: "يفوق التوقعات دائمًا", color: "#2563EB" },
  { label: "يلبي التوقعات", color: "#16A34A" },
  { label: "يلبي التوقعات أحيانًا", color: "#CA8A04" },
  { label: "أقل من المتوقع", color: "#DC2626" },
];

// سلّم تقديرات المرحلة الابتدائية الجديد — نصّي بالكامل، بلا أي لون
// (يطابق تمامًا ELEMENTARY_ACADEMIC_BANDS في logic.py بالـ Backend؛ أي
// تعديل مستقبلي على الحدود هناك يجب أن ينعكس هنا يدويًا أيضًا).
const ELEMENTARY_SCALE_LEGEND = [
  { range: "٨٥٪ فأكثر", label: "ممتاز" },
  { range: "٧٥٪ – أقل من ٨٥٪", label: "جيد جدًا" },
  { range: "٦٥٪ – أقل من ٧٥٪", label: "جيد" },
  { range: "٥٠٪ – أقل من ٦٥٪", label: "مقبول" },
  { range: "أقل من ٥٠٪", label: "دون المستوى" },
];

// يختار لون خط أبيض أو داكن حسب سطوع لون الخلفية — يضمن وضوح الرقم/النص
// دائمًا فوق أي لون تعبئة من نظام الألوان (بما فيها الألوان الفاتحة مثل
// "لم تُرصد" الرمادية، والتي يكون فيها الأبيض غير واضح).
function contrastTextColor(hex) {
  if (!hex) return "#111827";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#111827" : "#FFFFFF";
}

export default function Certificates() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("");
  const [certType, setCertType] = useState("semester1");
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  // إظهار/إخفاء الدرجات في الشهادة — يؤثر فقط على مراحل الروضة/الابتدائي
  // (نظام الألوان)؛ لا تأثير له على باقي المراحل، حيث تظهر الدرجة كالمعتاد
  // دائمًا بلا تلوين.
  const [showGrades, setShowGrades] = useState(true);

  useEffect(() => {
    if (!grade) return;
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  useEffect(() => {
    if (!grade) { setStudents([]); return; }
    api.get("/students", { params: {
      stage: ctx.stage, year: ctx.year, grade, ...(cls ? { class_name: cls } : {}),
    } }).then(({ data }) => setStudents(data))
       .catch((e) => toast.error(formatApiError(e)));
  }, [ctx.stage, ctx.year, grade, cls]);

  const view = async (stu) => {
    try {
      const { data } = await api.get("/certificates/student", {
        params: { stage: ctx.stage, year: ctx.year, student_id: stu.id, grade, cert_type: certType },
      });
      setSelected({ ...data, showGrades });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateBulk = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setBulkGenerating(true);
    try {
      const { data } = await api.get("/certificates/class", {
        params: {
          stage: ctx.stage, year: ctx.year, grade, cert_type: certType,
          ...(cls ? { class_name: cls } : {}),
        },
      });
      if (!data.certificates?.length) {
        toast.error("لا توجد شهادات في هذا التحديد");
        return;
      }
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const nodes = [];
      for (const c of data.certificates) {
        const wrap = document.createElement("div");
        // أبعاد صفحة A4 بالضبط (عند 96 نقطة/بوصة) — نفس فلسفة البرنامج
        // المكتبي الذي يرسم مباشرة على صفحة A4 كاملة من البداية، بدل
        // بناء عنصر بارتفاع حر ثم محاولة "تحجيمه" ليناسب الصفحة لاحقًا
        // (وهذا الأخير كان السبب الجذري لكل مشاكل التصغير/التمدد).
        wrap.style.width = "794px";
        wrap.style.height = "1123px";
        wrap.style.overflow = "hidden";
        wrap.style.background = "#ffffff";
        wrap.style.lineHeight = "1";
        wrap.innerHTML = "";
        container.appendChild(wrap);
        // React portal alternative: use temp component render via createRoot
        const { createRoot } = await import("react-dom/client");
        await new Promise((resolve) => {
          const root = createRoot(wrap);
          root.render(<CertificateBody data={{ ...c, school: data.school, use_color_bands: data.use_color_bands, show_elementary_scale: data.show_elementary_scale, stage: data.stage, year: data.year, grade: data.grade, cert_type: data.cert_type, showGrades }} />);
          // ننتظر اكتمال تحميل الخطوط فعليًا (لا مهلة عمياء) — التقاط
          // الصورة قبل اكتمال تحميل الخط العربي من Google Fonts يجعل
          // المتصفح يرسم بخط بديل لا يدعم اتصال الحروف العربية بشكل
          // صحيح، فتظهر الحروف مقطّعة أو منفصلة في الملف المُصدَّر.
          document.fonts.ready.then(() => setTimeout(resolve, 150));
        });
        nodes.push(wrap);
      }
      await downloadBulkCertificatesPdf(
        nodes,
        `شهادات-${grade}-${CERT_LABELS[certType]}.pdf`,
      );
      document.body.removeChild(container);
      toast.success(`تم توليد ${data.certificates.length} شهادة`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkGenerating(false);
    }
  };

  if (selected) return <CertificateView data={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <PageHeader
        icon={Award}
        overline="التقارير والشهادات"
        title="شهادات الطلاب"
        subtitle="إصدار شهادات فردية أو جماعية مطابقة لتصميم البرنامج المكتبي، بتوقيع تلقائي وشعارات المدرسة."
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <SF label="الصف" value={grade} onChange={setGrade} options={grades} />
        <SF label="الفصل" value={cls || "__all__"}
             onChange={(v) => setCls(v === "__all__" ? "" : v)}
             options={["__all__", ...classes]}
             labels={{ __all__: "كل الفصول" }} disabled={!grade} />
        <SF label="نوع الشهادة" value={certType} onChange={setCertType}
             options={["semester1", "semester2", "end_year"]}
             labels={{ semester1: "الفصل الأول", semester2: "الفصل الثاني", end_year: "نهاية العام" }} />
        <Button onClick={generateBulk} disabled={!grade || bulkGenerating}
                 className="h-10" variant="outline"
                 data-testid="cert-bulk-btn">
          <Layers className="h-4 w-4 me-1.5" />
          {bulkGenerating ? "جارٍ التوليد…" : "PDF لكل الطلاب"}
        </Button>
        {/* يظهر تأثيره فقط في شهادات الروضة/الابتدائي (نظام الألوان) — عمود
            الدرجة يُملأ دائمًا بلون التقدير المناسب؛ هذا الخيار يتحكم فقط
            في ظهور الرقم نفسه فوق التلوين أو إخفائه (تلوين بلا رقم). */}
        <label className="flex items-center gap-2 text-sm md:col-span-4 cursor-pointer">
          <input type="checkbox" checked={showGrades} onChange={(e) => setShowGrades(e.target.checked)}
                 className="h-4 w-4 accent-primary" data-testid="cert-show-grades" />
          إظهار الدرجات في الشهادة (يُطبَّق فقط على الروضة/الابتدائي — العمود يُلوَّن دائمًا حسب التقدير سواء أظهرت الرقم أو أخفيته)
        </label>
      </div>

      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-border">
            <tr>
              <th className="px-3 py-3 text-start text-[11px] font-semibold text-muted-foreground uppercase w-16">م.</th>
              <th className="px-3 py-3 text-start text-[11px] font-semibold text-muted-foreground uppercase">اسم الطالب</th>
              <th className="px-3 py-3 text-start text-[11px] font-semibold text-muted-foreground uppercase">الفصل</th>
              <th className="px-3 py-3 text-end text-[11px] font-semibold text-muted-foreground uppercase">جلوس</th>
              <th className="px-3 py-3 text-end text-[11px] font-semibold text-muted-foreground uppercase w-40">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">اختر صفًا لعرض الطلاب.</td></tr>
            )}
            {students.map((s, i) => (
              <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{i + 1}</td>
                <td className="px-3 py-2.5 font-medium">{s.name}</td>
                <td className="px-3 py-2.5">{s.class || "—"}</td>
                <td className="px-3 py-2.5 text-end tabular-nums">{s.seat_number || "—"}</td>
                <td className="px-3 py-2.5 text-end">
                  <Button size="sm" onClick={() => view(s)} data-testid={`cert-view-${s.id}`}>
                    عرض الشهادة
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SF({ label, value, onChange, options, labels, disabled }) {
  const opts = options.filter((o) => o !== "" && o != null)
    .map((o) => ({ value: o, label: labels?.[o] ?? o }));
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="اختر" /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function CertificateView({ data, onBack }) {
  const nodeRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const dl = async () => {
    if (!nodeRef.current) return;
    setDownloading(true);
    try {
      const fname = `${data.student?.name || "شهادة"}-${CERT_LABELS[data.cert_type]}.pdf`;
      await downloadCertificatePdf(nodeRef.current, fname);
      toast.success("تم تنزيل الشهادة");
    } catch (e) { toast.error("فشل التنزيل"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between no-print">
        <Button variant="outline" onClick={onBack} data-testid="cert-back">
          <ArrowLeft className="h-4 w-4 me-1.5" /> عودة
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="cert-print">
            <Printer className="h-4 w-4 me-1.5" /> طباعة
          </Button>
          <Button onClick={dl} disabled={downloading} data-testid="cert-download">
            <FileDown className="h-4 w-4 me-1.5" />
            {downloading ? "جارٍ التنزيل…" : "تنزيل PDF"}
          </Button>
        </div>
      </div>

      <div ref={nodeRef} data-testid="cert-view" style={{ width: CERT_WIDTH_PX, height: CERT_HEIGHT_PX, overflow: "hidden" }}>
        <CertificateBody data={data} />
      </div>
    </div>
  );
}

/** خلية بيانات واحدة (تسمية + قيمة) داخل جدول بيانات الطالب. */
function Cell({ label, value, ltr, last }) {
  return (
    <div className={`px-3 py-1.5 flex items-center justify-between gap-2 border-t border-slate-200 ${last ? "" : "border-e"}`}>
      <span className="text-primary font-semibold whitespace-nowrap">{label}</span>
      <span className={ltr ? "tabular-nums" : ""} dir={ltr ? "ltr" : undefined}>{value || "—"}</span>
    </div>
  );
}

// أبعاد شهادة بنسبة A4 دقيقة عند 96dpi (210×297مم) — نفس نسبة صفحة
// البرنامج المكتبي بالحرف، حتى تُطبَع الشهادة بلا أي تصغير أو توسيط
// اجتهادي عند تحويلها لـPDF لاحقًا.
export const CERT_WIDTH_PX = 794;
export const CERT_HEIGHT_PX = 1123;

function CertificateBody({ data }) {
  const { rows, student, school, cert_type, grade, year, use_color_bands, show_elementary_scale, totals, passed, next_grade, showGrades } = data;

  // "ناجح ومنقول إلى..." — فقط لشهادة نهاية العام. next_grade يأتي جاهزًا
  // من الـ Backend (next_grade_for_promotion في routes.py)، ويشمل الانتقال
  // بين المراحل عند آخر صف في كل مرحلة (KG2 ← الأول الابتدائي، السادس
  // الابتدائي ← الأول الإعدادي، الثالث الإعدادي ← الأول الثانوي) تمامًا
  // كالبرنامج المكتبي — وليس فقط الصف التالي داخل نفس المرحلة.
  let promotionText = null;
  if (cert_type === "end_year") {
    promotionText = passed ? (next_grade ? `ناجح ومنقول إلى ${next_grade}` : "ناجح") : "غير ناجح";
  }

  // ملحوظات الشهادة — تأتي جاهزة من الـ Backend (get_matching_certificate_notes)،
  // مبنية على ما كتبه المستخدم فعليًا في الإعدادات لهذا الصف/القسم، وليست
  // نصوصًا مبرمَجة هنا. يمكن أن يوجد أكثر من ملحوظة مطابقة في آنٍ واحد.
  const certNotes = data.certificate_notes || [];

  // الروضة/الابتدائي: لا مجموع ولا نسبة إطلاقًا — تُعرَض المواد كما وردت
  // من الـ Backend (بترتيبها المخصَّص) بلا أي تعديل إضافي. هذا الشرط
  // يستخدم (use_color_bands || show_elementary_scale) معًا — فبعد إلغاء
  // الألوان عن الابتدائي، use_color_bands وحدها أصبحت خاصة بالروضة فقط،
  // لكن "لا مجموع" كان قرارًا سابقًا مستقلًا يشمل الابتدائي أيضًا ولم
  // يُطلَب تغييره الآن.
  // الإعدادي/الثانوي: تُقسَّم المواد لمجموعتين (تُضاف للمجموع / لا تُضاف)،
  // ويُدرَج صف "المجموع" بينهما تلقائيًا — موضعه ثابت دائمًا، وليس جزءًا
  // من ترتيب المستخدم القابل للتعديل. لا نسبة مئوية تظهر في أي الحالتين.
  const hideAggregateTotal = use_color_bands || show_elementary_scale;
  const totalRows = rows.filter((r) => r.type !== "outcome" && r.add_to_total !== false);
  const nonTotalRows = rows.filter((r) => r.type === "outcome" || r.add_to_total === false);
  const displayRows = hideAggregateTotal ? rows : [...totalRows, { __isTotalRow: true }, ...nonTotalRows];
  const n = displayRows.length;

  // مستويات ضغط تدريجية تحاكي البرنامج المكتبي بالحرف: حجم مريح مصمَّم
  // لاستيعاب حوالي 15 مادة دون أي تمديد أو تكبير غير ضروري، وابتداءً
  // من المادة السادسة عشرة فقط يبدأ تقليص الصفوف والخط تدريجيًا — لا
  // تُملأ المساحة المتاحة بالكامل أبدًا عند قلّة عدد المواد؛ يُترك ما
  // يتبقّى من ارتفاع الشهادة الثابت فراغًا طبيعيًا أسفل قسم التوقيعات.
  const LEVELS = [
    { max: 15, rowH: 24, font: 12 },
    { max: 20, rowH: 19, font: 11 },
    { max: 26, rowH: 16, font: 10 },
    { max: Infinity, rowH: 13, font: 9 },
  ];
  const level = LEVELS.find((l) => n <= l.max) || LEVELS[LEVELS.length - 1];
  const rowH = level.rowH;
  const rowFont = level.font;

  return (
    <div
      className="mx-auto bg-white border-4 border-primary/70 rounded-xl p-6 shadow-sm flex flex-col"
      style={{ width: CERT_WIDTH_PX, height: CERT_HEIGHT_PX, boxSizing: "border-box" }}
    >
      <div className="border-b-2 border-primary/50 pb-3 text-center space-y-0.5 relative">
        {school?.ministry_logo && (
          <img src={school.ministry_logo} alt="شعار الوزارة"
               className="absolute top-0 start-0 h-14 w-14 object-contain" crossOrigin="anonymous" />
        )}
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار المدرسة"
               className="absolute top-0 end-0 h-14 w-14 object-contain" crossOrigin="anonymous" />
        )}
        <div className="text-primary font-display text-lg font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
        {school?.education_dept && <div className="text-primary font-medium text-sm">{school.education_dept}</div>}
        {school?.office_name && <div className="text-primary font-medium text-sm">{school.office_name}</div>}
        <div className="text-primary font-display text-base font-bold">{school?.school_name || "المدرسة"}</div>
      </div>

      <div className="mt-3 text-center">
        <h2 className="font-display text-base font-bold text-primary">
          {(school?.document_texts?.cert_title || "مستخرج رسمي بنتيجة طالب في تقويم الأداء التحصيلي")}
        </h2>
        <div className="mt-1 text-sm">{CERT_LABELS[cert_type] || ""} — {grade}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">العام الدراسي: {year}</div>
      </div>

      <div className="mt-3 border border-primary/40 rounded-md overflow-hidden text-sm">
        <div className="px-3 py-1.5 bg-primary/10 font-semibold text-primary text-center border-b border-primary/30">
          {student?.name || "—"}
        </div>
        <div className="grid grid-cols-2">
          <Cell label="رقم الجلوس" value={student?.seat_number} />
          <Cell label="الجنسية" value="مصري" />
          <Cell label="القسم" value={student?.section} />
          <Cell label="تاريخ الميلاد" value={student?.birthdate} />
          <Cell label="الرقم القومي" value={student?.national_id} ltr />
          <Cell label="الصف" value={grade} />
          <Cell label="اللغة الأجنبية الأولى" value={student?.foreign_lang1} />
          <Cell label="اللغة الأجنبية الثانية" value={student?.foreign_lang2 || "-"} />
          <Cell label="السفارة" value={school?.embassy_name || "-"} last />
          <Cell label="رقم الهوية" value={student?.identity_number || "-"} last />
        </div>
      </div>

      {/* جدول المواد بحجم مريح ثابت (لا يتمدد بلا داعٍ)، يضيق تلقائيًا
          فقط إذا تجاوز عدد المواد ~15 مادة. */}
      {/* جدول المواد بحجم مريح ثابت (لا يتمدد بلا داعٍ)، يضيق تلقائيًا
          فقط إذا تجاوز عدد المواد ~15 مادة. عمود "التقدير" الرابع يظهر
          فقط للمرحلة الابتدائية (سلّم النسب النصي الجديد، بلا أي تلوين). */}
      <table className="w-full mt-3 border-collapse" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr className="bg-primary text-white" style={{ height: rowH + 8 }}>
            <th className="px-2 text-start border border-primary" style={{ fontSize: rowFont + 1, width: show_elementary_scale ? "38%" : "50%" }}>المادة</th>
            <th className="px-2 text-end border border-primary" style={{ fontSize: rowFont + 1, width: show_elementary_scale ? "19%" : "25%" }}>النهاية</th>
            <th className="px-2 text-end border border-primary" style={{ fontSize: rowFont + 1, width: show_elementary_scale ? "19%" : "25%" }}>الدرجة</th>
            {show_elementary_scale && (
              <th className="px-2 text-center border border-primary" style={{ fontSize: rowFont + 1, width: "24%" }}>التقدير</th>
            )}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((r, i) => (
            r.__isTotalRow ? (
              <tr key="total-row" style={{ height: rowH }} className="bg-primary/10">
                <td className="px-2 border border-slate-200 font-bold text-primary" style={{ fontSize: rowFont }}>المجموع</td>
                <td className="px-2 text-end tabular-nums border border-slate-200 font-bold text-primary" style={{ fontSize: rowFont }}>{totals.max}</td>
                <td className="px-2 text-end tabular-nums border border-slate-200 font-bold text-primary" style={{ fontSize: rowFont }}>{totals.sum.toFixed(1)}</td>
              </tr>
            ) : (
              <tr key={i} style={{ height: rowH }}>
                <td className="px-2 border border-slate-200" style={{ fontSize: rowFont }}>{r.subject}</td>
                <td className="px-2 text-end tabular-nums border border-slate-200" style={{ fontSize: rowFont }}>{r.max_score}</td>
                {/* عمود الدرجة: يُملأ بلون التقدير المناسب دائمًا في مرحلة
                    الروضة فقط (نظام الألوان)، بصرف النظر عن حالة "إظهار
                    الدرجات" — الخيار يتحكم فقط بظهور الرقم نفسه فوق التلوين.
                    الابتدائي: بلا أي تلوين إطلاقًا (نظام الألوان أُلغي منها)
                    — الرقم يظهر عاديًا، والتقدير يظهر نصًّا في عمود مستقل.
                    الإعدادي/الثانوي: بلا تغيير كالمعتاد. */}
                <td className="px-2 text-end tabular-nums font-semibold border border-slate-200"
                     style={{
                       fontSize: rowFont,
                       ...(use_color_bands ? { backgroundColor: r.color, color: contrastTextColor(r.color) } : {}),
                     }}>
                  {use_color_bands ? (showGrades !== false ? r.total : "") : r.total}
                </td>
                {show_elementary_scale && (
                  <td className="px-2 text-center border border-slate-200 font-medium" style={{ fontSize: rowFont }}>
                    {r.label || "—"}
                  </td>
                )}
              </tr>
            )
          ))}
        </tbody>
      </table>

      {/* كشاف التقديرات — لمرحلة الروضة فقط (ألوان GRADE_BANDS، مطابق لما
          كان معروضًا في البرنامج المكتبي). لا يظهر للابتدائي بعد إلغاء
          الألوان عنها — بدلًا منه جدول نصّي لسلّم النسب الجديد أدناه. */}
      {use_color_bands && (
        <div className="mt-3 flex rounded-md overflow-hidden border border-primary/20 text-[11px] font-bold text-white text-center">
          {GRADE_BAND_LEGEND.map((b) => (
            <div key={b.label} className="flex-1 py-1.5 px-1" style={{ backgroundColor: b.color }}>
              {b.label}
            </div>
          ))}
        </div>
      )}

      {/* بيان سلّم التقديرات النصّي — للمرحلة الابتدائية فقط، يحل محل
          الكشاف الملوَّن السابق تمامًا، بلا أي لون. */}
      {show_elementary_scale && (
        <div className="mt-3 border border-primary/20 rounded-md overflow-hidden text-[11px]">
          <div className="bg-primary/10 text-primary font-bold text-center py-1 border-b border-primary/20">سلّم التقديرات</div>
          <div className="grid grid-cols-5 text-center font-medium">
            {ELEMENTARY_SCALE_LEGEND.map((s, i) => (
              <div key={s.label} className={`py-1.5 px-1 ${i > 0 ? "border-e border-primary/15" : ""}`}>
                <div className="text-[10px] text-muted-foreground">{s.range}</div>
                <div>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ملحوظات الشهادة القابلة للتخصيص — من الإعدادات ← ملاحظات الشهادة.
          تُعرَض كل ملحوظة مطابقة في صندوقها الخاص، بصرف النظر عن الفصل
          الدراسي أو المرحلة (الشرط بالكامل من جانب الـ Backend). */}
      {certNotes.map((note, i) => (
        <div key={i} className="mt-3 text-center py-2 px-3 rounded-md font-semibold text-[12px] leading-relaxed bg-slate-50 text-slate-700 border border-slate-300">
          {note}
        </div>
      ))}

      {/* "ناجح ومنقول إلى..." — لشهادة نهاية العام فقط (لكل المراحل، بما
          فيها الروضة/الابتدائي)، بعد جدول المواد مباشرة، زي البرنامج
          المكتبي بالضبط. */}
      {promotionText && (
        <div className={`mt-3 text-center py-2 rounded-md font-bold text-sm ${
          passed ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {promotionText}
        </div>
      )}

      <div className="mt-2 text-end text-xs text-muted-foreground">
        تم تحرير هذه الشهادة بتاريخ {new Date().toLocaleDateString("en-GB")}
      </div>

      {/* قسم التوقيعات — عمود واحد موحَّد: مشرف المسار المصري يمينًا،
          مصادقة الأداء المعرفي وسطًا، ومدير المدرسة يسارًا، مع مساحة
          كافية للتوقيع والختم تحت كل عنوان. */}
      <div className="mt-6 grid grid-cols-3 text-center text-sm gap-4">
        <div>
          <div className="text-primary font-semibold">{school?.field_labels?.egypt_supervisor || "المشرف"}</div>
          <div className="font-semibold text-[13px] mt-1">{school?.egypt_supervisor || "—"}</div>
          <div className="text-muted-foreground mt-8">( _______________ )</div>
        </div>
        <div>
          <div className="text-primary font-semibold">
            {school?.document_texts?.performance_certification || "مصادقة الأداء المعرفي"}
          </div>
          <div className="text-muted-foreground mt-8">( _______________ )</div>
        </div>
        <div>
          <div className="text-primary font-semibold">{school?.field_labels?.school_manager || "مدير المدرسة"}</div>
          <div className="font-semibold text-[13px] mt-1">{school?.school_manager || "—"}</div>
          <div className="text-muted-foreground mt-8">( _______________ )</div>
        </div>
      </div>
    </div>
  );
}
